import {
  generateColorString,
  generateEscapedTaskSettingsCommandString,
  generateEscapedTokenString,
  generateMediaLoadString,
  generateMediaPlaybackString,
  generateMediaStatusReportString,
  generateMediaVisibilityString,
  generateNotesString,
  generateShowMessageCommandString,
  generateSUDString,
  generateThemeCommandString,
  generateVOCString,
} from '../utils/serializer';
import { MediaTypes } from '../commands/practitioner/enums/MediaTypes';
import { PlaybackType } from '../commands/practitioner/enums/PlaybackType';
import { MediaStates } from '../commands/practitioner/enums/MediaStates';
import { ShowMessageCommandTypes } from '../commands/practitioner/enums/ShowMessageCommandTypes';
import { SourceProjection } from '../commands/practitioner/enums/SourceProjection';
import baseURL from '../utils/consts';

const request = async (url: string, options: any) => {
  try {
    const res = await fetch(url, options);
    // 304 Not Modified and 204 No Content have empty bodies per HTTP spec — there is nothing to parse.
    if (res.status === 304 || res.status === 204) return null;
    return await res.json();
  } catch (err) {
    console.log(err);
    return null;
  }
};

const getClientToken = async (sessionId: string, treatmentId: string, identity: string = 'client') => {
  const url = `${baseURL}/sessions/twilio-video/token?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&identity=${identity}`;
  return request(url, { method: 'POST' });
};

const getToken = async (sessionId: string, treatmentId: string, isSendToClient: boolean = true) => {
  const url = `${baseURL}/sessions/twilio-video/create-room?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  const data = await request(url, { method: 'POST' });
  const { token } = data;
  if (isSendToClient) {
    const clientToken = await getClientToken(sessionId, treatmentId, 'client');
    await sendTokenToClient(sessionId, treatmentId, clientToken);
  }
  return token;
};

const sendTokenToClient = async (sessionId: string, treatmentId: string, token: string) => {
  const command = generateEscapedTokenString(token);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendThemeCommand = async (sessionId: string, treatmentId: string, mode: boolean) => {
  const command = generateThemeCommandString(mode);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendTaskSettingsCommandToClient = async (sessionId: string, treatmentId: string, value: string) => {
  const command = generateEscapedTaskSettingsCommandString(value);
  // console.log('SENDING COMMAND:', command);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendShowMessageCommand = async (sessionId: string, treatmentId: string, type: ShowMessageCommandTypes) => {
  const command = generateShowMessageCommandString(type);
  // console.log('SENDING COMMAND:', command);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendSaveNotesCommand = async (sessionId: string, treatmentId: string, value: string) => {
  const command = generateNotesString(value);
  // console.log('SENDING NOTES:', command);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendSUDCommand = async (sessionId: string, treatmentId: string, value: string) => {
  const command = generateSUDString(value);
  // console.log('SENDING SUD:', command);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendVOCCommand = async (sessionId: string, treatmentId: string, value: string) => {
  const command = generateVOCString(value);
  // console.log('SENDING VOC:', command);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendReactionResponseCommand = async (sessionId: string, treatmentId: string, value: boolean) => {
  const command = value ? 'RPF:1' : 'RPF:0';
  // console.log('SENDING REACTION:', command);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendAutopilotDifficultyCommand = async (sessionId: string, treatmentId: string, value: number) => {
  const command = `APD:${value}`;
  // console.log('SENDING AUTO PILOT DIFFICULTY:', command);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendAutopilotDifficultyToClientCommand = async (
  sessionId: string,
  treatmentId: string,
  value: number,
  seed: number
) => {
  const command = `APD:${value},${seed}`;
  // console.log('SENDING AUTO PILOT DIFFICULTY:', command);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendManualColorCommand = async (sessionId: string, treatmentId: string, value: string) => {
  const command = generateColorString(value);
  // console.log('SENDING Color command:', command);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendLoadMedia = async (
  sessionId: string,
  treatmentId: string,
  mediaURI: string,
  mediaType: MediaTypes,
  projectType: SourceProjection,
  stereo: string,
  rotation: number = 0
) => {
  const command = generateMediaLoadString(mediaURI, mediaType, projectType, stereo, rotation);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendMediaVisibility = async (sessionId: string, treatmentId: string, mediaURI: string, isVisible: boolean) => {
  const command = generateMediaVisibilityString(mediaURI, isVisible);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendMediaStatusReport = async (
  sessionId: string,
  treatmentId: string,
  mediaURI: string,
  status: MediaStates,
  progress: number = 1.0
) => {
  const command = generateMediaStatusReportString(mediaURI, status, progress);
  const url = `${baseURL}/sessions/pushPractitionerCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const sendMediaCommand = async (
  sessionId: string,
  treatmentId: string,
  mediaURI: string,
  playbackType: PlaybackType,
  value: number
) => {
  const command = generateMediaPlaybackString(mediaURI, playbackType, value);
  const url = `${baseURL}/sessions/pushClientCommand?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}&command=${command}`;
  return fetch(url, { method: 'POST' });
};

const fetchCurrentCommandsForPractitioner = async (sessionId: string, treatmentId: string) => {
  const url = `${baseURL}/sessions/practitionerCommands?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  return request(url, { method: 'GET' });
};

const getHasNewSession = async (sessionId: string, treatmentId: string) => {
  const url = `${baseURL}/sessions/hasNewSession?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  return request(url, { method: 'GET' });
};

const fetchCurrentCommandsForClient = async (sessionId: string, treatmentId: string) => {
  const url = `${baseURL}/sessions/clientCommands?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  return request(url, { method: 'GET' });
};

const updateSessionData = (sessionId: string, treatmentId: string, attributes: any, endDate: string | null = null) => {
  const filter = encodeURI(JSON.stringify({ sessionUUID: sessionId, treatmentUUID: treatmentId }));
  const url = `${baseURL}/sessions/upsertWithWhere?where=${filter}`;
  const body = {
    sessionUUID: sessionId,
    treatmentUUID: treatmentId,
    end_date: endDate,
    attributes,
  };
  return request(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
};

const checkIfSessionExists = async (sessionId: string, treatmentId: string) => {
  const session = await getCurrentState(sessionId, treatmentId);
  return typeof session === 'object';
};

const checkIfPractitionerHasValidToken = (token: string, sessionId: string, treatmentId: string) => {
  const filter = {
    where: {
      sessionUUID: sessionId,
      treatmentUUID: treatmentId,
    },
  };
  const url = `${baseURL}/sessions?access_token=${token}&filter=${JSON.stringify(filter)}`;
  return fetch(url, { method: 'GET' });
};

const getClientCommands = (sessionId: string, treatmentId: string) => {
  const url = `${baseURL}/sessions/clientCommands?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  return request(url, { method: 'GET' });
};

const getCurrentState = (sessionId: string, treatmentId: string) => {
  const url = `${baseURL}/sessions/currentState?sessionUUID=${sessionId}&treatmentUUID=${treatmentId}`;
  return request(url, { method: 'GET' });
};

const patchReceivedProperty = (generatedUUID: string) => {
  const url = `${baseURL}/remotetreatmentconfigurations/received`;
  return request(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      generated_UUID: generatedUUID,
    }),
  });
};

const BackendService = {
  getToken,
  generateEscapedTaskSettingsCommandString,
  sendTaskSettingsCommandToClient,
  sendSaveNotesCommand,
  sendManualColorCommand,
  fetchCurrentCommandsForPractitioner: fetchCurrentCommandsForPractitioner,
  fetchCurrentCommandsForClient: fetchCurrentCommandsForClient,
  sendSUDCommand,
  sendVOCCommand,
  sendReactionResponseCommand,
  sendAutopilotDifficultyCommand,
  updateSessionData,
  checkIfSessionExists,
  checkIfPractitionerHasValidToken,
  sendLoadMedia,
  sendMediaCommand,
  getClientCommands,
  sendMediaVisibility,
  sendMediaStatusReport,
  getCurrentState,
  patchReceivedProperty,
  sendThemeCommand,
  sendShowMessageCommand,
  sendAutopilotDifficultyToClientCommand,
  getHasNewSession,
};

export default BackendService;
